import type { SearchableAppPage } from '@/config/appNavigation'
import type { SearchEntityHit, SearchMetadataKind, SearchValueHit } from '../models'
import {
  RESULT_ROW_HEIGHT,
  RESULT_VIRTUAL_OVERSCAN_ROWS,
  RESULT_VIRTUAL_WINDOW_ROWS,
  VISIBLE_CAP,
  type FilterKey,
  type ResultKind,
} from './constants'
import { buildMetadataRow, buildPageRow, buildValueRow, isMetadataKind } from './rows'
import type { NavigableItem, RenderedSectionRows, Row, Section, ToggleAction } from './types'

/** One metadata section's rows: the response's own plus any "See more" pages. */
export interface MetadataSectionSource {
  rows: SearchEntityHit[]
  /** The response's count, when it sent one. */
  total: number | undefined
  /** Rows shown on the All tab; undefined until "See more" is first pressed. */
  revealed: number | undefined
  hasMore: boolean
}

export interface GlobalSearchSectionSources {
  activeFilter: FilterKey
  pages: SearchableAppPage[]
  metadata: Record<SearchMetadataKind, MetadataSectionSource>
  records: { rows: Row[]; count: number }
  values: { rows: SearchValueHit[]; count: number }
}

/** How the result list is shown: the All tab's collapsed and expanded sections. */
export interface SectionView {
  isAllTab: boolean
  expanded: ReadonlySet<string>
  collapsed: ReadonlySet<string>
}

export interface RowRange {
  start: number
  end: number
}

const METADATA_KINDS = ['tables', 'views', 'fields'] as const
const METADATA_TITLES: Record<SearchMetadataKind, string> = {
  tables: 'Tables',
  views: 'Views',
  fields: 'Fields',
}

/** The sections the active tab shows, in order: pages, tables, views, fields, records, values. */
export function buildGlobalSearchSections({
  activeFilter,
  pages,
  metadata,
  records,
  values,
}: GlobalSearchSectionSources): Section[] {
  const isAllTab = activeFilter === 'all'
  const show = (kind: ResultKind) => isAllTab || activeFilter === kind
  const sections: Section[] = []

  if (show('pages') && pages.length > 0) {
    sections.push({
      key: 'pages',
      kind: 'pages',
      title: 'Pages',
      count: pages.length,
      rows: pages.map(buildPageRow),
    })
  }

  for (const kind of METADATA_KINDS) {
    if (show(kind) && metadata[kind].rows.length > 0) {
      sections.push(buildMetadataSection(kind, metadata[kind], isAllTab))
    }
  }

  if (show('records') && records.rows.length > 0) {
    sections.push({
      key: 'records',
      kind: 'records',
      title: 'Records',
      count: records.count,
      rows: records.rows,
    })
  }

  if (show('values') && values.rows.length > 0) {
    sections.push({
      key: 'values',
      kind: 'values',
      title: 'Values',
      count: values.count,
      rows: values.rows.map(buildValueRow),
    })
  }

  return sections
}

function buildMetadataSection(
  kind: SearchMetadataKind,
  { rows, total: responseTotal, revealed, hasMore }: MetadataSectionSource,
  isAllTab: boolean
): Section {
  const total = responseTotal ?? rows.length
  const visibleLimit = isAllTab ? (revealed ?? VISIBLE_CAP) : rows.length
  return {
    key: kind,
    kind,
    title: METADATA_TITLES[kind],
    count: total,
    visibleLimit,
    canRevealMore: isAllTab && rows.length > visibleLimit,
    canSeeLess:
      isAllTab && visibleLimit > VISIBLE_CAP && visibleLimit >= total && rows.length >= total,
    canFetchMore: hasMore,
    rows: rows
      .map((item) => buildMetadataRow(kind, item))
      .filter((row): row is Row => row !== null),
  }
}

export function getVisibleSectionRows(
  section: Section,
  { isAllTab, expanded, collapsed }: SectionView
): Row[] {
  if (isAllTab && collapsed.has(section.key)) return []
  if (isMetadataKind(section.kind) && section.visibleLimit !== undefined) {
    return section.rows.slice(0, section.visibleLimit)
  }
  if (!isAllTab) return section.rows
  return expanded.has(section.key) ? section.rows : section.rows.slice(0, VISIBLE_CAP)
}

/** The "See more" / "See less" control under an All-tab section, if it has one. */
export function getSectionToggleAction(section: Section): ToggleAction | null {
  if (isMetadataKind(section.kind)) {
    if (section.canSeeLess) return 'less'
    return section.canRevealMore || section.canFetchMore ? 'more' : null
  }
  return section.rows.length > VISIBLE_CAP || section.count > VISIBLE_CAP ? 'toggle' : null
}

export const getSectionHeaderKey = (sectionKey: string) => `section:${sectionKey}`

export const getSectionToggleKey = (sectionKey: string, action: ToggleAction) =>
  action === 'toggle' ? `toggle:${sectionKey}` : `toggle:${sectionKey}:${action}`

/** Everything the arrow keys walk through on the All tab: headers, rows, and toggles. */
export function buildNavigableItems(sections: Section[], view: SectionView): NavigableItem[] {
  return sections.flatMap((section) => {
    const items: NavigableItem[] = view.isAllTab
      ? [{ type: 'section', key: getSectionHeaderKey(section.key), sectionKey: section.key }]
      : []
    for (const row of getVisibleSectionRows(section, view)) {
      items.push({ type: 'row', key: row.key, row })
    }
    const action =
      view.isAllTab && !view.collapsed.has(section.key) ? getSectionToggleAction(section) : null
    if (action) {
      items.push({
        type: 'toggle',
        key: getSectionToggleKey(section.key, action),
        sectionKey: section.key,
        action,
      })
    }
    return items
  })
}

/** Rows in a result tab's list: metadata and empty-search records count rows not loaded yet. */
export function getResultTabRowCount(section: Section, isPreview: boolean): number {
  if (isMetadataKind(section.kind) || (section.kind === 'records' && isPreview)) {
    return Math.max(section.count, section.rows.length)
  }
  return section.rows.length
}

/** The rows a result tab renders at a scroll position, with overscan above. */
export function getVirtualRange(rowCount: number, scrollTop: number): RowRange {
  const start = Math.max(
    0,
    Math.floor(Math.max(0, scrollTop - 12) / RESULT_ROW_HEIGHT) - RESULT_VIRTUAL_OVERSCAN_ROWS
  )
  const end = Math.min(rowCount, start + RESULT_VIRTUAL_WINDOW_ROWS)
  return { start, end }
}

export function getWindowedRows(
  range: RowRange,
  rowCount: number,
  getRowAt: (index: number) => Row | null
): RenderedSectionRows {
  return {
    rows: Array.from({ length: Math.max(0, range.end - range.start) }, (_, offset) => {
      const index = range.start + offset
      return { index, row: getRowAt(index) }
    }),
    beforeHeight: range.start * RESULT_ROW_HEIGHT,
    afterHeight: Math.max(0, rowCount - range.end) * RESULT_ROW_HEIGHT,
  }
}
